import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Trash2, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useEscapeKey from '../hooks/useEscapeKey';
import { resolveImageUrlBusted } from '../utils/urlHelper';

const AccountSwitchModal = ({ isOpen, onClose }) => {
  useEscapeKey(onClose, isOpen);
  const navigate = useNavigate();
  const { savedAccounts, switchAccount, removeSavedAccount, refreshSavedAccounts, user } = useAuth();

  React.useEffect(() => {
    if (isOpen && typeof refreshSavedAccounts === 'function') {
      refreshSavedAccounts();
    }
  }, [isOpen, refreshSavedAccounts]);

  const otherAccounts = React.useMemo(() => {
    if (!Array.isArray(savedAccounts)) return [];
    return savedAccounts.filter((a) => a.id !== user?.id);
  }, [savedAccounts, user?.id]);

  const handleSwitch = (account) => {
    onClose();
    switchAccount(account);
  };

  const handleRemove = (e, accountId) => {
    e.stopPropagation();
    removeSavedAccount(accountId);
  };

  const handleAddAccount = () => {
    onClose();
    navigate('/login?switch_account=1');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.92, opacity: 0 }}
            className="card-glass w-full max-w-sm p-5 space-y-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base font-bold text-text">Cambiar de cuenta</h2>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-glass-hover transition-colors"
              >
                <X size={18} className="text-text/70" />
              </button>
            </div>

            {otherAccounts.length === 0 ? (
              <p className="text-sm text-text/60 text-center py-2">
                Aún no tienes otras cuentas guardadas.
              </p>
            ) : (
              <div className="space-y-2">
                {otherAccounts.map((account) => (
                  <button
                    key={account.id}
                    type="button"
                    onClick={() => handleSwitch(account)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-glass hover:bg-glass-hover transition-colors text-left group"
                  >
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center shrink-0 overflow-hidden">
                      {account.avatar_url ? (
                        <img
                          src={resolveImageUrlBusted(account.avatar_url, account.addedAt || Date.now())}
                          alt={account.display_name || account.username}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-sm font-bold text-accent uppercase">
                          {(account.display_name || account.username || '?').charAt(0)}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-text truncate">
                        {account.display_name || account.username}
                      </div>
                      <div className="text-[11px] text-text/60 truncate">
                        @{account.username}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <LogIn size={16} className="text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <button
                        type="button"
                        onClick={(e) => handleRemove(e, account.id)}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-text/40 hover:text-red-400 transition-colors"
                        title="Eliminar cuenta guardada"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleAddAccount}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-text/20 hover:border-accent/40 hover:bg-accent/5 transition-colors text-sm font-semibold text-text/80"
            >
              <UserPlus size={18} className="text-accent" />
              Agregar cuenta
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AccountSwitchModal;
